import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Row, Col, Card, Button, Badge, Table, Alert, Spinner, Modal, Toast, Form } from "react-bootstrap";
import { FaArrowLeft, FaHome, FaMap, FaBuilding, FaRulerCombined, FaMoneyBillWave, FaUpload, FaExclamationTriangle, FaExclamationCircle, FaCheckCircle } from "react-icons/fa";
import { BsCheck, BsX } from "react-icons/bs";
import "../../styles/PropertyDetail.css";
import BookingRequestForm from "../../components/BookingRequestForm";
import { useAuth } from "../../context/AuthContext";
import api, { API_URL } from '../../services/api';

// AeroIndustrialTypeDetail component
const AeroIndustrialTypeDetail = () => {
  const { typeId } = useParams();
  const [propertyData, setPropertyData] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showAgentBookingForm, setShowAgentBookingForm] = useState(false);
  const [lotToBook, setLotToBook] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [agentFormData, setAgentFormData] = useState({
    agentName: "",
    phone: "",
    email: "",
    description: ""
  });
  const [paymentCheque, setPaymentCheque] = useState(null);
  const [identityCards, setIdentityCards] = useState([]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Add this debug function
  const processLotData = (rawLot) => {
    return {
      ...rawLot,
      area: typeof rawLot.area === 'number' ? rawLot.area : 
            rawLot.type === 'CORNER (A)' ? 201 : 186 // Default values based on type
    };
  };

  useEffect(() => {
    const fetchLots = async () => {
    try {
        // Set default property data
          setPropertyData({
          id: 'aero-industrial-hub',
          name: 'Aero Industrial Hub',
          description: 'A modern industrial development with various lot types for businesses.',
          image: '/images/aero-industrial-hub.jpg'
          });
      
        // Fetch lots data with type filter
        console.log(`Fetching lots for type: ${typeId}`);
        const lotsResponse = await api.get(`properties/aeroindustrial/lots/${typeId}`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        console.log('Lots response:', lotsResponse.data);

        // Check if response is valid
        if (!lotsResponse.data) {
          throw new Error("No lots data received from server");
        }

        // Handle different response formats
        let lotsData;
        if (Array.isArray(lotsResponse.data)) {
          lotsData = lotsResponse.data;
        } else if (lotsResponse.data.lots && Array.isArray(lotsResponse.data.lots)) {
          lotsData = lotsResponse.data.lots;
        } else if (lotsResponse.data.data && Array.isArray(lotsResponse.data.data)) {
          lotsData = lotsResponse.data.data;
        } else if (typeof lotsResponse.data === 'object') {
          // If it's an object, try to extract lots data
          const possibleArrays = Object.values(lotsResponse.data).filter(Array.isArray);
          if (possibleArrays.length > 0) {
            lotsData = possibleArrays[0];
          } else {
            throw new Error("Could not find lots array in response data");
          }
        } else {
          throw new Error("Invalid lots data format received");
        }

        // Process and normalize the lots data
        const processedLots = lotsData.map(lot => {
          // Ensure we have a valid lot object
          if (!lot || typeof lot !== 'object') {
            console.warn('Invalid lot data:', lot);
            return null;
          }

          try {
            const processedLot = processLotData(lot);
            return processedLot;
      } catch (err) {
            console.error('Error processing lot:', lot, err);
            return null;
          }
        }).filter(lot => lot !== null); // Remove any invalid lots
        
        console.log('Final processed lots:', processedLots);
        
        if (processedLots.length === 0) {
          setError("No valid lots data found for this type");
        } else {
          setLots(processedLots);
          setError(null); // Clear any previous errors
        }
        
      } catch (err) {
        console.error("Error fetching data:", err);
        let errorMessage = "Failed to load property data. ";
        
        if (err.response) {
          errorMessage += err.response.data?.message || `Server error ${err.response.status}`;
          console.error('Response error:', {
            status: err.response.status,
            data: err.response.data,
            headers: err.response.headers,
            url: err.response.config?.url
          });
        } else if (err.request) {
          errorMessage += "No response received from server. Please check your internet connection.";
          console.error('Request error:', {
            url: err.config?.url,
            method: err.config?.method,
            headers: err.config?.headers
          });
        } else {
          errorMessage += err.message || "An unknown error occurred.";
          console.error('Setup error:', err);
        }
        
        setError(errorMessage);
        setLots([]);
      } finally {
      setLoading(false);
    }
  };
  
    fetchLots();
  }, [typeId]);

  // Update the refresh function to use the same endpoint as initial fetch
  const refreshLots = async () => {
    setLoading(true);
    try {
      const timestamp = new Date().getTime();
      // Use the same endpoint as the initial fetch
      const lotsResponse = await api.get(`properties/aeroindustrial/lots/${typeId}?_t=${timestamp}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        validateStatus: function (status) {
          return status < 500;
        }
      });
      
      console.log('Refresh lots response:', lotsResponse.data);

      // Check if response is valid
      if (!lotsResponse.data) {
        throw new Error("No lots data received from server");
      }

      // Handle different response formats (same logic as initial fetch)
      let lotsData;
      if (Array.isArray(lotsResponse.data)) {
        lotsData = lotsResponse.data;
      } else if (lotsResponse.data.lots && Array.isArray(lotsResponse.data.lots)) {
        lotsData = lotsResponse.data.lots;
      } else if (lotsResponse.data.data && Array.isArray(lotsResponse.data.data)) {
        lotsData = lotsResponse.data.data;
      } else if (typeof lotsResponse.data === 'object') {
        // If it's an object, try to extract lots data
        const possibleArrays = Object.values(lotsResponse.data).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          lotsData = possibleArrays[0];
        } else {
          throw new Error("Could not find lots array in response data");
        }
      } else {
        throw new Error("Invalid lots data format received");
      }

      // Process and normalize the lots data (same as initial fetch)
      const processedLots = lotsData.map(lot => {
        // Ensure we have a valid lot object
        if (!lot || typeof lot !== 'object') {
          console.warn('Invalid lot data:', lot);
          return null;
        }

        try {
          const processedLot = processLotData(lot);
          return processedLot;
        } catch (err) {
          console.error('Error processing lot:', lot, err);
          return null;
        }
      }).filter(lot => lot !== null); // Remove any invalid lots
      
      console.log('Final refreshed lots:', processedLots);
      
      if (processedLots.length === 0) {
        setError("No valid lots data found for this type");
      } else {
        setLots(processedLots);
        setError(null); // Clear any previous errors
      }
      
      console.log("Successfully refreshed lots data");
    } catch (err) {
      console.error("Error refreshing lot data:", err);
      let errorMessage = "Failed to refresh lot data. ";
      
      if (err.response) {
        errorMessage += err.response.data?.message || `Server error ${err.response.status}`;
        console.error('Refresh response error:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers,
          url: err.response.config?.url
        });
      } else if (err.request) {
        errorMessage += "No response received from server. Please check your internet connection.";
        console.error('Refresh request error:', {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers
        });
      } else {
        errorMessage += err.message || "An unknown error occurred.";
        console.error('Refresh setup error:', err);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (typeof amount === 'string' && amount.includes(',')) {
      // If the amount is already a formatted string (like "1,150,000.00")
      return `RM ${amount}`;
    }
    return new Intl.NumberFormat('en-MY', { 
      style: 'currency', 
      currency: 'MYR',
      maximumFractionDigits: 0
    }).format(amount);
    };
    
  // Helper function to format area
  const formatArea = (area) => {
    if (!area && area !== 0) return "N/A";
    return area.toLocaleString('en-MY');
  };
    
  // Get type name based on ID
  const getTypeName = () => {
    switch (typeId) {
      case "A":
        return "Type A (Corner)";
      case "B":
        return "Type B (Intermediate)";
      case "C":
        return "Type C (End Lot)";
      default:
        return `Type ${typeId}`;
    }
  };

  // Get type description based on ID
  const getTypeDescription = () => {
    switch (typeId) {
      case "A":
        return "Corner lot with excellent visibility and access from multiple sides.";
      case "B":
        return "Standard intermediate lot with good access and optimal space utilization.";
      case "C":
        return "End lot with additional side access and visibility.";
      default:
        return "Industrial lot with good accessibility.";
    }
  };

  // Function to handle lot selection
  const handleSelectLot = (lot) => {
    setSelectedLot(lot === selectedLot ? null : lot);
  };
  
  // Function to handle the Book Now button click
  const handleBookNowClick = (lot) => {
    // Prevent default action if any
    if (window.event) {
      window.event.preventDefault();
      window.event.stopPropagation();
    }
    
    // Check if user is logged in
    if (!currentUser) {
      // Redirect to login if not logged in
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    
    // Set the lot to book
    setLotToBook(lot);
    
    // Check if the user is an agent
    const isAgent = currentUser.role === 'agent' || currentUser.role === 'agent_admin';
    
    if (isAgent) {
      // For agents, show the agent booking form
      setAgentFormData({
        agentName: currentUser.name || "",
        phone: currentUser.phone || "",
        email: currentUser.email || "",
        description: ""
      });
      setPaymentCheque(null);
      setIdentityCards([]);
      setShowAgentBookingForm(true);
    } else {
      // For regular users, show the regular booking form modal
      setShowBookingForm(true);
      
      // Focus the first input element when the modal opens
      setTimeout(() => {
        const firstInput = document.querySelector('.booking-request-form input:first-of-type');
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);
    }
  };
  
  // Function to handle input changes in the agent form
  const handleAgentFormChange = (e) => {
    const { name, value } = e.target;
    setAgentFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Function to handle file upload for payment cheque
  const handleChequeUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentCheque(e.target.files[0]);
    }
  };
  
  // Function to handle multiple file upload for identity cards
  const handleIdentityCardUpload = (e) => {
    if (e.target.files) {
      setIdentityCards(Array.from(e.target.files));
    }
  };
  
  // Function to submit the agent booking form
  const handleAgentBookingSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setBookingSubmitting(true);
      
      // Get the token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Validate that both IC front and back are uploaded
      if (identityCards.length < 2) {
        setToastMessage("Both front and back sides of IC are required");
        setShowToast(true);
        setBookingSubmitting(false);
        return;
      }
      
      // Get lot details
      const lotType = lotToBook.type || (typeof typeId === 'string' ? typeId.toUpperCase() : '');
      const lotNumber = lotToBook.lotNumber || '';
      
      // Create compound property ID
      const propertyId = `type-${lotType.toLowerCase()}-${lotNumber}`;
      
      console.log(`Attempting to book property with ID: ${propertyId}`);
      
      // Check if we have all required files
      if (identityCards.length === 0) {
        setToastMessage("Please upload at least one identity card image (front and back)");
        setShowToast(true);
        setBookingSubmitting(false);
        return;
      }
      
      // Use simpler JSON request first without file uploads (more reliable)
      const jsonData = {
        propertyId: propertyId,
        requestMessage: `Agent: ${agentFormData.agentName}\nPhone: ${agentFormData.phone}\nEmail: ${agentFormData.email}\nDescription: ${agentFormData.description}`,
        contactPhone: agentFormData.phone,
        userId: currentUser._id,
        status: "pending_admin"
      };
      
      console.log('Sending booking data:', jsonData);
      
      // Debug: Log the API URL being used
      console.log('API_URL from services/api.js:', API_URL);
      console.log('Current location:', window.location.href);
      
      // First try with basic JSON data - use api instance instead of axios directly
      let response;
      try {
        response = await api.post('/bookings/property', jsonData);
      } catch (apiError) {
        // If the api instance fails, try with explicit full URL
        console.warn('API instance failed, trying with explicit URL:', apiError.message);
        
        if (apiError.message === 'Network Error' || apiError.code === 'ERR_NETWORK') {
          // For network errors, try with current origin
          const fallbackUrl = `${window.location.origin}/api/bookings/property`;
          console.log('Trying fallback URL:', fallbackUrl);
          
          const token = localStorage.getItem('token');
          response = await fetch(fallbackUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(jsonData)
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          // Convert fetch response to axios-like format
          response = {
            data: await response.json(),
            status: response.status,
            statusText: response.statusText
          };
        } else {
          throw apiError; // Re-throw non-network errors
        }
      }
      
      console.log('Initial booking response:', response.data);
      
      // If successful, then handle file uploads if there are any
      if (response.data && response.data._id) {
        const bookingId = response.data._id;
        
        // Now handle file uploads if needed
        if (paymentCheque || identityCards.length > 0) {
          // Create a FormData for the files
          const filesFormData = new FormData();
          
          // Add the booking ID
          filesFormData.append('bookingId', bookingId);
          
          // Add payment cheque if available
          if (paymentCheque) {
            filesFormData.append('cheque', paymentCheque);
            filesFormData.append('documentType', 'cheque');
          }
          
          // Upload IC files (backend may handle only one file at a time)
          if (identityCards.length > 0) {
            // Flag to track if we've already mentioned this in error message
            let warnedAboutMultipleFiles = false;
            
            for (let i = 0; i < identityCards.length; i++) {
              try {
                const icFormData = new FormData();
                icFormData.append('bookingId', bookingId);
                icFormData.append('document', identityCards[i]);
                icFormData.append('documentType', i === 0 ? 'ic_front' : 'ic_back');
                
                await api.post('/uploads/document', icFormData);
                
                console.log(`Uploaded IC file ${i+1}`);
              } catch (uploadErr) {
                console.error(`Error uploading IC file ${i+1}:`, uploadErr);
                if (!warnedAboutMultipleFiles) {
                  setToastMessage(`Booking created, but there was an issue uploading identity card file ${i+1}. The booking is still valid.`);
                  setShowToast(true);
                  warnedAboutMultipleFiles = true;
                }
              }
            }
          }
          
          // Try to upload cheque separately if available
          if (paymentCheque) {
            try {
              const chequeFormData = new FormData();
              chequeFormData.append('bookingId', bookingId);
              chequeFormData.append('document', paymentCheque);
              chequeFormData.append('documentType', 'cheque');
              
              await api.post('/uploads/document', chequeFormData);
              
              console.log('Uploaded cheque file');
            } catch (chequeErr) {
              console.error('Error uploading cheque:', chequeErr);
              setToastMessage('Booking created, but there was an issue uploading the cheque. The booking is still valid.');
              setShowToast(true);
            }
          }
        }
      }
      
      // Handle success
      setToastMessage("Booking successfully created and sent to admin for review");
      setShowToast(true);
      setShowAgentBookingForm(false);
      
      // Clear form data
      setAgentFormData({
        agentName: "",
        phone: "",
        email: "",
        description: ""
      });
      setPaymentCheque(null);
      setIdentityCards([]);
      setLotToBook(null);
      
      // Refresh lots data immediately to show updated status
      console.log("Refreshing lots data after successful booking...");
      await refreshLots();
    } catch (err) {
      console.error("Error creating agent booking:", err);
      let errorMessage = "Failed to create booking. ";
      
      // Extract detailed error information
      if (err.response) {
        console.error("Error response:", err.response);
        
        errorMessage += err.response.data?.message || `Server error ${err.response.status}`;
      } else if (err.request) {
        console.error("Error request:", err.request);
        errorMessage += "No response received from server.";
      } else {
        console.error("Error details:", err);
        errorMessage += err.message || "Unknown error occurred.";
      }
      
      setToastMessage(errorMessage);
      setShowToast(true);
    } finally {
      setBookingSubmitting(false);
    }
  };
  
  // Function to handle agent form close
  const handleAgentFormClose = () => {
    setShowAgentBookingForm(false);
    setLotToBook(null);
  };
  
  // Function to handle booking form close
  const handleBookingFormClose = () => {
    // First hide the form
    setShowBookingForm(false);
    
    // Remove modal-specific body classes and backdrop after a short delay
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const modalBackdrops = document.querySelectorAll('.modal-backdrop');
      modalBackdrops.forEach(backdrop => backdrop.remove());
      
      // Clear the selected lot last
      setLotToBook(null);
    }, 300);
  };
  
  // Function to handle booking form success
  const handleBookingFormSuccess = async () => {
    // First hide the form
    setShowBookingForm(false);
    
    // Remove modal-specific elements after a short delay
    setTimeout(async () => {
      document.body.classList.remove('modal-open');
      const modalBackdrops = document.querySelectorAll('.modal-backdrop');
      modalBackdrops.forEach(backdrop => backdrop.remove());
      
      // Show success message
      setToastMessage("Booking request successfully submitted!");
      setShowToast(true);
      
      // Clear the selected lot
      setLotToBook(null);
      
      // Refresh lots data immediately (for regular users, status shouldn't change but refresh anyway)
      console.log("Refreshing lots data after successful booking request...");
      await refreshLots();
    }, 300);
  };

  // Calculate availability statistics
  const calculateStats = () => {
    if (!Array.isArray(lots)) return {};
    
    const total = lots.length;
    const available = lots.filter(lot => lot.status === 'available').length;
    const reserved = lots.filter(lot => lot.status === 'reserved').length;
    const booked = lots.filter(lot => lot.status === 'booked').length;
    const sold = lots.filter(lot => lot.status === 'sold').length;
    const availabilityPercentage = Math.round((available / total) * 100);
    
    return { total, available, reserved, booked, sold, availabilityPercentage };
  };

  // Render availability overview section
  const renderAvailabilityOverview = () => {
    const stats = calculateStats();
    
    return (
      <div className="availability-overview">
        <h3>Availability Overview</h3>
        <div className="availability-stats">
          <div className="stat-card available">
            <div className="stat-number">{stats.available}</div>
            <div className="stat-label">Available</div>
          </div>
          <div className="stat-card reserved">
            <div className="stat-number">{stats.reserved}</div>
            <div className="stat-label">Reserved</div>
          </div>
          <div className="stat-card booked">
            <div className="stat-number">{stats.booked}</div>
            <div className="stat-label">Booked</div>
          </div>
          <div className="stat-card sold">
            <div className="stat-number">{stats.sold}</div>
            <div className="stat-label">Sold</div>
          </div>
          <div className="stat-card total">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
        </div>
        <div className="availability-status">
          <div className="availability-header">
            <div className="availability-title">Availability Status</div>
            <div className="availability-percentage">{stats.availabilityPercentage}% Available</div>
          </div>
          <div className="status-bar">
            <div 
              className="status-bar-fill" 
              style={{ width: `${stats.availabilityPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Add this helper function near the top of the component, after other helper functions
  const calculateLandArea = (lot) => {
    const sqmToSqft = 10.7639;
    let areaSqm = null;
    let areaSqft = null;

    // Helper to check if a number is valid
    const isValidNumber = (num) => typeof num === 'number' && !isNaN(num) && num > 0;

    // The area field in database is in square meters
    if (isValidNumber(lot.area)) {
      areaSqm = lot.area;  // This is already in square meters (186 or 201)
      areaSqft = Math.round(lot.area * sqmToSqft);  // Convert to square feet
    } else {
      // Fallback to type-based default values
      areaSqm = lot.type === 'CORNER (A)' ? 201 : 186;
      areaSqft = Math.round(areaSqm * sqmToSqft);
    }

    return {
      sqm: areaSqm,
      sqft: areaSqft
    };
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-4 property-detail-container">
      {error && <Alert variant="danger">{error}</Alert>}
      
      <div className="page-navigation mb-4">
        <Link to="/aero-industrial-hub" className="back-link">
          <FaArrowLeft className="me-2" /> Back to AeroIndustrial Hub
        </Link>
      </div>

      <Row className="mb-4">
        <Col lg={12}>
          <div className="property-header">
            <h1>{getTypeName()}</h1>
            <p className="lead">{getTypeDescription()}</p>
          </div>
          
          {renderAvailabilityOverview()}
        </Col>
      </Row>
      
      <div className="property-lot-types">
        <h2>Available Lots</h2>
        
        <Row>
          <Col lg={selectedLot ? 8 : 12}>
            <div className="lots-table-container">
              <table className="lots-table">
                <thead>
                  <tr>
                    <th>Lot Number</th>
                    <th>Land Area (sqm)</th>
                    <th>Land Area (sqft)</th>
                    <th>Price (RM)</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.length > 0 ? (
                    lots.map((lot) => {
                      const landArea = calculateLandArea(lot);
                      return (
                      <tr 
                          key={lot._id || lot.lotNumber} 
                        className={selectedLot && selectedLot._id === lot._id ? 'selected-row' : ''}
                        onClick={() => handleSelectLot(lot)}
                      >
                        <td className="lot-number-col">{lot.lotNumber}</td>
                          <td>{landArea.sqm} sqm</td>
                          <td>{landArea.sqft} sqft</td>
                        <td>{formatCurrency(lot.price)}</td>
                        <td>
                          {lot.status.toLowerCase() === "available" ? (
                            <span className="status-tag status-available">Available</span>
                          ) : lot.status.toLowerCase() === "booked" ? (
                            <span className="status-tag status-booked">Booked</span>
                          ) : lot.status.toLowerCase() === "reserved" ? (
                            <span className="status-tag status-reserved">Reserved</span>
                          ) : lot.status.toLowerCase() === "locked" ? (
                            <span className="status-tag status-sold">Locked</span>
                          ) : (
                            <span className="status-tag status-sold">Sold</span>
                          )}
                        </td>
                        <td>
                          {lot.status.toLowerCase() === "available" && currentUser?.role !== 'super_admin' ? (
                            <button 
                              className="action-button" 
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row selection when clicking the button
                                handleBookNowClick(lot);
                              }}
                            >
                              Book Now
                            </button>
                          ) : lot.status.toLowerCase() === "available" && currentUser?.role === 'super_admin' ? (
                            <span className="status-tag status-available">Available</span>
                          ) : (
                            <button className="action-button not-available" disabled>
                              {lot.status.toLowerCase() === "booked" ? "Booked" : 
                               lot.status.toLowerCase() === "reserved" ? "Reserved" :
                               lot.status.toLowerCase() === "locked" ? "Locked" : "Sold"}
                            </button>
                          )}
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No lots available for this type
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Col>
          
          {selectedLot && (
            <Col lg={4}>
              <div className="selected-lot-details">
                <h3>Lot {selectedLot.lotNumber} Details</h3>
                <div className="selected-lot-info">
                  <div className="info-row">
                    <span className="info-label"><FaRulerCombined /> Land Area (sqm):</span>
                    <span className="info-value">
                      {calculateLandArea(selectedLot).sqm} sqm
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label"><FaRulerCombined /> Land Area (sqft):</span>
                    <span className="info-value">
                      {calculateLandArea(selectedLot).sqft} sqft
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label"><FaMoneyBillWave /> Price:</span>
                    <span className="info-value">{formatCurrency(selectedLot.price)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Status:</span>
                    <span className={`info-value status-${selectedLot.status.toLowerCase()}`}>
                      {selectedLot.status}
                    </span>
                  </div>
                  
                  {selectedLot.status.toLowerCase() === "available" && currentUser?.role !== 'super_admin' && (
                    <div className="mt-4 text-center">
                      <Button 
                        variant="primary" 
                        className="w-100"
                        onClick={() => handleBookNowClick(selectedLot)}
                      >
                        Book This Lot Now
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
      
      <div className="cta-section mt-5">
        <h2>Interested in the AeroIndustrial Hub?</h2>
        <p>Contact our team for more information or to schedule a site visit.</p>
        <div className="cta-buttons">
          <a href="mailto:aeroindustrialhub@gmail.com" className="btn btn-primary">
            Email Us
          </a>
          <a href="tel:+60162820967" className="btn btn-outline-primary">
            Call +60 16-282 0967
          </a>
        </div>
      </div>
      
      {/* Agent Booking Form Modal */}
      <Modal
        show={showAgentBookingForm}
        onHide={handleAgentFormClose}
        size="lg"
        backdrop="static"
        className="agent-booking-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Agent Booking for Lot {lotToBook?.lotNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAgentBookingSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Agent Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="agentName"
                value={agentFormData.agentName}
                onChange={handleAgentFormChange}
                placeholder="Name of agent who will receive commission"
                required
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={agentFormData.phone}
                    onChange={handleAgentFormChange}
                    placeholder="Contact phone number"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={agentFormData.email}
                    onChange={handleAgentFormChange}
                    placeholder="Contact email"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Payment Cheque</Form.Label>
              <div className="upload-input-wrapper">
                <Form.Control
                  type="file"
                  onChange={handleChequeUpload}
                  accept="image/*,application/pdf"
                />
                <div className="upload-instructions">
                  <FaUpload /> Upload payment cheque (Image or PDF)
                </div>
              </div>
              {paymentCheque && (
                <div className="file-preview mt-2">
                  <span className="file-name">{paymentCheque.name}</span>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="text-danger" 
                    onClick={() => setPaymentCheque(null)}
                  >
                    <BsX />
                  </Button>
                </div>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Identity Card (Front & Back) <span className="text-danger">*</span></Form.Label>
              
              <div className="ic-card-uploads">
                <div className="ic-upload-side">
                  <div className="ic-upload-title">Front Side <span className="text-danger">*</span></div>
                  <div className="upload-input-wrapper">
                    <Form.Control
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          // Keep the back card if it exists
                          const newFiles = identityCards.length > 1 ? [e.target.files[0], identityCards[1]] : [e.target.files[0]];
                          setIdentityCards(newFiles);
                        }
                      }}
                      accept="image/*"
                    />
                    <div className="upload-instructions">
                      <FaUpload /> Upload front of IC
                    </div>
                  </div>
                  
                  {identityCards.length > 0 && (
                    <div className="file-preview mt-2">
                      <span className="file-name">{identityCards[0]?.name}</span>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="text-danger" 
                        onClick={() => {
                          const newFiles = [...identityCards];
                          newFiles.splice(0, 1);
                          setIdentityCards(newFiles);
                        }}
                      >
                        <BsX />
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="ic-upload-side">
                  <div className="ic-upload-title">Back Side <span className="text-danger">*</span></div>
                  <div className="upload-input-wrapper">
                    <Form.Control
                      type="file"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          // Keep the front card if it exists
                          const newFiles = identityCards.length > 0 ? 
                            [identityCards[0], e.target.files[0]] : 
                            [e.target.files[0]]; // If no front card yet, use this as first
                          setIdentityCards(newFiles);
                        }
                      }}
                      accept="image/*"
                    />
                    <div className="upload-instructions">
                      <FaUpload /> Upload back of IC
                    </div>
                  </div>
                  
                  {identityCards.length > 1 && (
                    <div className="file-preview mt-2">
                      <span className="file-name">{identityCards[1]?.name}</span>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="text-danger" 
                        onClick={() => {
                          const newFiles = [...identityCards];
                          newFiles.splice(1, 1);
                          setIdentityCards(newFiles);
                        }}
                      >
                        <BsX />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="upload-status mt-2">
                {identityCards.length === 0 && (
                  <div className="text-danger">
                    <FaExclamationTriangle className="me-1" /> Both front & back of IC are required
                  </div>
                )}
                {identityCards.length === 1 && (
                  <div className="text-warning">
                    <FaExclamationCircle className="me-1" /> Please upload both front & back of IC
                  </div>
                )}
                {identityCards.length === 2 && (
                  <div className="text-success">
                    <FaCheckCircle className="me-1" /> Both front & back uploaded successfully
                  </div>
                )}
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={agentFormData.description}
                onChange={handleAgentFormChange}
                placeholder="Additional details or notes about this booking"
                rows={3}
              />
            </Form.Group>
            
            <div className="text-end mt-4">
              <Button 
                variant="secondary" 
                className="me-2"
                onClick={handleAgentFormClose}
                disabled={bookingSubmitting}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={bookingSubmitting}
              >
                {bookingSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit Booking'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Booking Request Form Modal */}
      <Modal
        show={showBookingForm}
        onHide={handleBookingFormClose}
        size="lg"

        backdrop="static"
        className="booking-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Book Lot {lotToBook?.lotNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {lotToBook && (
            <BookingRequestForm
              lot={lotToBook}
              onSuccess={handleBookingFormSuccess}
              onCancel={handleBookingFormClose}
            />
          )}
        </Modal.Body>
      </Modal>
      
      {/* Toast notification for booking */}
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        delay={3000} 
        autohide
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999
        }}
      >
        <Toast.Header>
          <strong className="me-auto">Booking Notification</strong>
        </Toast.Header>
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default AeroIndustrialTypeDetail; 